import React from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {shallowEqual, useSelector} from 'react-redux';
import ButtonCreateClient from '../ButtonCreateClient';
import {StyledFieldInput} from '../../../_common/components/FieldInput';
import MultipleTooltip from '../../../_common/components/MultipleTooltip';
import {integerValueCanStartFromNull} from '../../../_common/utils/changeInputValue';
import {optionsCustomerStatus, optionsCustomerTypes, optionsGenders} from '../../constants/options';
import {useCustomerDetailsUIContext} from '../CustomerDetailsUIContext';
import Unit from '../../../_common/components/Unit';
import {StyledFieldSelect} from '../../../_common/components/FieldSelect';
import Spinner from '../../../_common/components/Spinner';

const prefix = 'customers-entity-popup-edit-customer';

const FormCustomerTypePerson = () => {
  const {
    formik: {
      values,
      setFieldValue,
      handleChange
    },
  } = useCustomerDetailsUIContext();

  return (
    <>
      <div className={'d-flex align-items-center justify-content-between mb-8'}>
        <StyledFieldInput
          fieldId={'firstName'}
          label={<FormattedMessage id={`${prefix}-first-name`} defaultMessage="First name"/>}
          value={values.firstName}
          onChange={handleChange}
          customContainerStyles={'w-50 mr-4'}
          icon={'/media/common/User_primary.svg'}
        />
        <StyledFieldInput
          fieldId={'lastName'}
          label={<FormattedMessage id={`${prefix}-last-name`} defaultMessage="Last name"/>}
          value={values.lastName}
          onChange={handleChange}
          customContainerStyles={'w-50 ml-4'}
          icon={'/media/common/User_primary.svg'}
        />
      </div>
      <div className={'d-flex align-items-center justify-content-between mb-8'}>
        <StyledFieldSelect
          fieldId={'genderID'}
          value={values.genderID}
          options={optionsGenders}
          label={<FormattedMessage id={`${prefix}-gender`} defaultMessage="Gender"/>}
          onChange={value => setFieldValue('genderID', value)}
          customContainerStyles={'w-50 mr-4'}
        />
        <StyledFieldInput
          fieldId={'idNumber'}
          label={<FormattedMessage id={`${prefix}-idNumber`} defaultMessage="ID number"/>}
          value={values.idNumber}
          onChange={e => setFieldValue('idNumber', integerValueCanStartFromNull(e))}
          customContainerStyles={'w-50 ml-4'}
          icon={'/media/common/Id-badge_primary.svg'}
        />
      </div>
    </>
  );
};

const FormCustomerTypesCompany = () => {
  const {
    formik: {
      values,
      setFieldValue,
      handleChange
    },
  } = useCustomerDetailsUIContext();

  return (
    <div className={'d-flex align-items-center justify-content-between mb-8'}>
      <StyledFieldInput
        fieldId={'firstName'}
        label={<FormattedMessage id={`${prefix}-name`} defaultMessage="Name"/>}
        value={values.firstName}
        onChange={handleChange}
        customContainerStyles={'w-50 mr-4'}
        icon={'/media/common/User_primary.svg'}
      />
      <StyledFieldInput
        fieldId={'idNumber'}
        label={<FormattedMessage id={`${prefix}-companyID`} defaultMessage="Company ID"/>}
        value={values.idNumber}
        onChange={e => setFieldValue('idNumber', integerValueCanStartFromNull(e))}
        customContainerStyles={'w-50 ml-4'}
        icon={'/media/common/Id-badge_primary.svg'}
      />
    </div>
  );
};

const FormCustomerTypeGroup = () => {
  const {
    formik: {
      values,
      setFieldValue,
      handleChange
    },
  } = useCustomerDetailsUIContext();

  return (
    <>
      <div className={'d-flex align-items-center justify-content-between mb-8'}>
        <StyledFieldInput
          fieldId={'firstName'}
          label={<FormattedMessage id={`${prefix}-groupName`} defaultMessage="Group name"/>}
          value={values.firstName}
          onChange={handleChange}
          customContainerStyles={'w-50 mr-4'}
          icon={'/media/common/User_primary.svg'}
        />
        <StyledFieldInput
          fieldId={'lastName'}
          label={<FormattedMessage id={`${prefix}-groupSubName`} defaultMessage="Group sub name"/>}
          value={values.lastName}
          onChange={handleChange}
          customContainerStyles={'w-50 ml-4'}
          icon={'/media/common/User_primary.svg'}
        />
      </div>
      <div className={'d-flex align-items-center justify-content-between mb-8'}>
        <StyledFieldInput
          fieldId={'idNumber'}
          label={<FormattedMessage id={`${prefix}-groupID`} defaultMessage="Group ID"/>}
          value={values.idNumber}
          onChange={e => setFieldValue('idNumber', integerValueCanStartFromNull(e))}
          customContainerStyles={'w-50 mr-4'}
          icon={'/media/common/Id-badge_primary.svg'}
        />
        <div className={'w-50 ml-4'}/>
      </div>
    </>
  );
};

const TabGeneralDetails = () => {
  const intl = useIntl();
  const {
    formik: {
      values,
      setFieldValue,
      handleChange,
    },
    greenInvoiceClientID,
    emailError,
    loading
  } = useCustomerDetailsUIContext();
  const {
    greenInvoiceApiKey,
    greenInvoiceSecret
  } = useSelector((state) => state.auth, shallowEqual);

  const canCreateCustomer = Boolean(greenInvoiceApiKey)
    && Boolean(greenInvoiceSecret)
    && !Boolean(greenInvoiceClientID)
    && !emailError;

  const renderFormByCustomerType = (customerType) => {
    switch (customerType) {
      case 1:
      case 2:
      case 3:
        return <FormCustomerTypesCompany/>;
      case 4:
        return <FormCustomerTypePerson/>;
      case 5:
        return <FormCustomerTypeGroup/>;
      default:
        return null;
    }
  };

  const resetFields = (customerType, setFieldValue) => {
    switch (customerType) {
      case 2:
      case 3:
        setFieldValue('lastName', '');
        setFieldValue('genderID', {value: null});
        break;
      case 4:
        break;
      case 5:
        setFieldValue('genderID', {value: null});
        break;
    }
  };

  return loading ? <Spinner customContainerStyles={'d-flex justify-content-center'} /> : (
    <>
      <div className={'d-flex justify-content-between w-75 mx-auto'}>
        <Unit
          text={intl.formatMessage({id: 'customer-details-page-subheader', defaultMessage: 'Customer'})}
          customClassnames={'w-50 mr-4'}
          icon={'/media/common/Folder_green.svg'}
        >
          <div className={'d-flex align-items-center justify-content-between mb-8'}>
            <StyledFieldSelect
              fieldId={'customerType'}
              value={values.customerType}
              options={optionsCustomerTypes}
              label={<FormattedMessage id={`${prefix}-type`} defaultMessage="Type"/>}
              onChange={value => {
                resetFields(value?.value, setFieldValue);
                setFieldValue('customerType', value);
              }}
              customContainerStyles={'w-50 mr-4'}
            />
            <StyledFieldSelect
              fieldId={'status'}
              value={values.status}
              options={optionsCustomerStatus}
              label={<FormattedMessage id={`${prefix}-status`} defaultMessage="Status"/>}
              onChange={value => setFieldValue('status', value)}
              customContainerStyles={'w-50 ml-4'}
            />
          </div>
          <div className={'d-flex align-items-center justify-content-between mb-8'}>
            <StyledFieldInput
              fieldId={'phone'}
              label={
                <div className={'d-flex align-items-center'}>
                  <span className={'mr-2'}><FormattedMessage id={`${prefix}-phone`} defaultMessage="Phone"/></span>
                  <MultipleTooltip
                    id={'phone'}
                    label={intl.formatMessage({
                      id: `${prefix}-phone-tooltip`,
                      defaultMessage: 'Please enter the phone number in digits only without special characters.'
                    })}
                    customIconClassName={'cursor-pointer position-static'}
                  />
                </div>
              }
              value={values.phone}
              onChange={e => setFieldValue('phone', integerValueCanStartFromNull(e))}
              customContainerStyles={'w-50 mr-4'}
              icon={'/media/common/Phone_primary.svg'}
            />
            <div className="position-relative w-50 ml-4">
              <StyledFieldInput
                type={'email'}
                fieldId={'email'}
                label={<FormattedMessage id={`${prefix}-email`} defaultMessage="Email"/>}
                value={values.email}
                onChange={handleChange}
                icon={'/media/common/Email_primary.svg'}
              />
              {emailError && (
                <div className={'text-danger font-weight-bold position-absolute bottom-0 pl-6'}>
                  <FormattedMessage id="invalid-email-message" defaultMessage="Invalid email"/>
                </div>
              )}
            </div>
          </div>
          <div className={'d-flex align-items-center justify-content-between mb-8'}>
            <StyledFieldInput
              fieldId={'address'}
              label={<FormattedMessage id={`${prefix}-address`} defaultMessage="Address"/>}
              value={values.address}
              onChange={handleChange}
              customContainerStyles={'w-50 mr-4'}
              icon={'/media/common/Address_primary.svg'}
            />
            <div className={'w-50 ml-4'}/>
          </div>
        </Unit>
        <Unit
          text={intl.formatMessage({id: 'additional', defaultMessage: 'Additional'})}
          customClassnames={'w-50 ml-4'}
          icon={'/media/common/User_orange.svg'}
        >
          {renderFormByCustomerType(values.customerType?.value)}
        </Unit>
      </div>
      {canCreateCustomer && <ButtonCreateClient/>}
    </>
  );
};

export default TabGeneralDetails;
